import React from 'react'
import { useStaticQuery, graphql, Link } from 'gatsby'
import Helmet from 'react-helmet'
import { AnchorLink } from 'gatsby-plugin-anchor-links'
import Layout from '../../components/Layout'
import ServiceFooter from '../../components/ServiceFooter'

const NNCamera = () => {
  const {
    site: {
      siteMetadata: { title },
    },
  } = useStaticQuery(graphql`
    {
      site {
        siteMetadata {
          title
        }
      }
    }
  `)

  return (
    <Layout>
      <Helmet>
        <html className="temp" lang="ja" />
        <title>{`NNカメラシステム | ${title}`}</title>
        <meta
          name="description"
          content="4KカメラとPKSHA独自のAIモデルを融合し、ナンバーや車体を識別する在車検知システムです。"
        />
      </Helmet>

      <div className="common-header clearfix">
        <p className="category pc-only">製品・サービス</p>
        <h1>NNカメラシステム</h1>
        <p className="category sp-only">製品・サービス</p>

        <div className="breadcrumb">
          <li>
            <Link to={`/`}>トップページ</Link>
          </li>
          /
          <li>
            <Link to={`/service`}>製品・サービス</Link>
          </li>
          /<li>NNカメラシステム</li>
        </div>
      </div>

      <div className="main-wrapper nncamera">
        <div className="row">
          <div className="container empty"></div>
        </div>

        <div className="row">
          <div className="h-wrapper">
            <h3>目次</h3>
          </div>
        </div>

        <div className="row">
          <div className="container table-of-contents">
            <ul>
              <li>
                <AnchorLink to={`/service/nncamera#about`}>
                  NNカメラシステムとは
                </AnchorLink>
              </li>
              <li>
                <AnchorLink to={`/service/nncamera#system`}>
                  アイテックのNNカメラシステム
                </AnchorLink>
                <ul>
                  <li>
                    <AnchorLink to={`/service/nncamera#feature-1`}>
                      特徴①幅広い環境で採用可能
                    </AnchorLink>
                    <ol>
                      <li>
                        <AnchorLink to={`/service/nncamera#feature-1-road`}>
                          路床自由
                        </AnchorLink>
                      </li>
                      <li>
                        <AnchorLink to={`/service/nncamera#feature-1-park`}>
                          ほとんどの駐車場で導入可能
                        </AnchorLink>
                      </li>
                      <li>
                        <AnchorLink to={`/service/nncamera#feature-1-snow`}>
                          雪国OK
                        </AnchorLink>
                      </li>
                    </ol>
                  </li>
                  <li>
                    <AnchorLink to={`/service/nncamera#feature-2`}>
                      特徴② 検知率の課題をクリア
                    </AnchorLink>
                  </li>
                  <li>
                    <AnchorLink to={`/service/nncamera#feature-3`}>
                      特徴③ 未精算出庫のリスク軽減
                    </AnchorLink>
                  </li>
                </ul>
              </li>
              <li>
                <AnchorLink to={`/service/nncamera#installation`}>
                  システム設置イメージ
                </AnchorLink>
              </li>
              <li>
                <AnchorLink to={`/service/nncamera#specs`}>
                  4Kカメラと周辺機器の仕様
                </AnchorLink>
              </li>
            </ul>
          </div>
        </div>

        <div className="row">
          <div className="h-wrapper" id="about">
            <h2>NNカメラシステムとは</h2>
            <div className="common-btn pc-only">
              <a
                href="/assets/files/whitepaper/itc-nncamera.pdf"
                target="_blank"
              >
                カタログダウンロード
              </a>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="container section-intro">
            <h4>
              4KカメラとPKSHA独自のAIモデルを融合し、ナンバーや車体を識別する在車検知システムです。
            </h4>
            <picture>
              <source
                srcSet="/assets/images/common/service-nncamera-01.webp"
                type="image/webp"
              />
              <img
                src="/assets/images/common/service-nncamera-01.png"
                alt="NNカメラシステムイメージ"
                loading="lazy"
              />
            </picture>
          </div>
        </div>

        <div className="row">
          <div className="h-wrapper" id="system">
            <h2>アイテックのNNカメラシステム</h2>
          </div>
        </div>

        <div className="row">
          <div className="container section-main">
            <h4>カメラ式ロックレスの導入におけるよくある不安を解消。</h4>
          </div>
        </div>

        <div className="row">
          <div className="h-wrapper" id="feature-1">
            <h3>特長1. 幅広い環境で採用可能</h3>
          </div>
        </div>

        <div className="row">
          <div className="container feature">
            <h5 className="feature-subtitle">
              アイテックの現場理解、ロックレス導入知見を活用し様々な環境に適応。
            </h5>
            <picture>
              <source
                srcSet="/assets/images/common/service-nncamera-02.webp"
                type="image/webp"
              />
              <img
                src="/assets/images/common/service-nncamera-02.png"
                alt="様々な環境への適応例"
                loading="lazy"
              />
            </picture>

            <div className="feature-item">
              <h5 className="feature-title" id="feature-1-road">
                1. 路床自由
              </h5>
              <p className="feature-desc">
                センサーを埋設できない駐車場に有効。
                <br />
                砂利敷き駐車場、コンクリート駐車場、緑化駐車場、ロードヒーティング埋設でもOK。
              </p>
            </div>

            <div className="feature-item">
              <h5 className="feature-title" id="feature-1-park">
                2. ほとんどの駐車場で導入可能
              </h5>
              <p className="feature-detail">
                施設の来客者向け駐車場や既設の自走式駐車場でも対応可能。車室単位の工事が不要で、設置工数が少ない。
              </p>
            </div>

            <div className="feature-item">
              <h5 className="feature-title" id="feature-1-snow">
                3. 雪国OK
              </h5>
              <p className="feature-detail">
                駐車中に雪がかまくら状に積もっても
                AIカメラが車体を検知。除雪作業も安全に実施可能。
              </p>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="h-wrapper" id="feature-2">
            <h3>特長2. 検知率の課題をクリア</h3>
          </div>
        </div>

        <div className="row">
          <div className="container feature">
            <h5 className="feature-subtitle">
              カメラが一時的に遮られても車両検知の継続を実現。
            </h5>
            <p className="feature-detail">
              弊社で実施した駐車場利用デー1,209件での実証実験で、車体検知率100％達成。
            </p>
            <p className="feature-detail">
              <small>
                ※2025年2月時点での検証。不適切な設置・反射・物理的な遮断がない環境での検知率を検証。
              </small>
            </p>
            <picture>
              <source
                srcSet="/assets/images/common/service-nncamera-03.webp"
                type="image/webp"
              />
              <img
                src="/assets/images/common/service-nncamera-03.png"
                alt="検知率実証実験グラフ"
                loading="lazy"
              />
            </picture>

            <div className="detection-images">
              <div className="detection-item">
                <p className="detection-label">車体検知画像（日中）</p>
                <picture>
                  <source
                    srcSet="/assets/images/common/service-nncamera-04.webp"
                    type="image/webp"
                  />
                  <img
                    src="/assets/images/common/service-nncamera-04.png"
                    alt="車体検知画像（日中）"
                    loading="lazy"
                  />
                </picture>
              </div>
              <div className="detection-item">
                <p className="detection-label">車体検知画像（夜間）</p>
                <picture>
                  <source
                    srcSet="/assets/images/common/service-nncamera-05.webp"
                    type="image/webp"
                  />
                  <img
                    src="/assets/images/common/service-nncamera-05.png"
                    alt="車体検知画像（夜間）"
                    loading="lazy"
                  />
                </picture>
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="h-wrapper" id="feature-3">
            <h3>特長3. 未精算出庫にも対応</h3>
          </div>
        </div>

        <div className="row">
          <div className="container feature">
            <h5 className="feature-subtitle">
              アイテックのロックレスの逃避率は、0.4%と業界最少水準。
            </h5>
            <p className="feature-detail">
              自社の出庫記録課で、未精算の出庫情報を一元管理することにより、PDCAサイクルを実現。
            </p>
            <p className="feature-detail">
              その結果、追跡業務の効率化や通知システムの改善が進み、逃避率は0.4%と業界最少水準。
            </p>
            <picture>
              <source
                srcSet="/assets/images/common/service-nncamera-06.webp"
                type="image/webp"
              />
              <img
                src="/assets/images/common/service-nncamera-06.png"
                alt="逃避率グラフ"
                loading="lazy"
              />
            </picture>
          </div>
        </div>

        <div className="row">
          <div className="h-wrapper" id="feature-4">
            <h3>特長4. その他</h3>
          </div>
        </div>

        <div className="row">
          <div className="container feature other-features">
            <div className="feature-item">
              <h5 className="feature-title">カメラのみで導入可能</h5>
              <p className="feature-detail">
                ロック板・センサーポール・ループコイルなどの物理的な設備は不要。4Kカメラのみで駐車場管理が可能なため、施工が簡易かつ低コスト。
              </p>
            </div>

            <div className="feature-item">
              <h5 className="feature-title">施工費を大幅に削減</h5>
              <p className="feature-detail">
                従来のナンバー認識ポールシステムと比べて、設置費用を抑えることが可能。
              </p>
            </div>

            <div className="feature-item">
              <h5 className="feature-title">技術融合による高性能システム</h5>
              <p className="feature-detail">
                ロックレス分野のパイオニアであるアイテックの知見と、PKSHAの最先端AI技術を融合し、他社にはない“かゆいところに手が届く”NNカメラシステムを実現。
              </p>
            </div>

            <div className="feature-item">
              <h5 className="feature-title">カメラ1台で最大5車室をカバー</h5>
              <p className="feature-detail">
                4Kカメラを車室正面に設置することで、最大5台分の車室を同時に撮影・管理可能。この広範囲カバーにより、さらにコスト削減が可能。
              </p>
            </div>

            <div className="feature-item">
              <h5 className="feature-title">柔軟なハイブリッド設置に対応</h5>
              <p className="feature-detail">
                横並びの多台数車室：4Kカメラで一括管理
                <br />
                離れた少台数の車室：従来のナンバー認識ポールで個別管理
                <br />
                ニーズに合わせて、4Kカメラとナンバー認識ポールを自由に構成できる、ハイブリッドな運用設計も可能。
              </p>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="h-wrapper" id="installation">
            <h3>ハイブリッド構成例</h3>
          </div>
        </div>

        <div className="row">
          <div className="container examples">
            <picture>
              <source
                srcSet="/assets/images/common/service-nncamera-07.webp"
                type="image/webp"
              />
              <img
                src="/assets/images/common/service-nncamera-07.png"
                alt="ハイブリッド構成図"
                loading="lazy"
              />
            </picture>
          </div>
        </div>

        <div className="row">
          <div className="h-wrapper">
            <h3>カメラ設置例</h3>
          </div>
        </div>

        <div className="row">
          <div className="container examples">
            <picture>
              <source
                srcSet="/assets/images/common/service-nncamera-08.webp"
                type="image/webp"
              />
              <img
                src="/assets/images/common/service-nncamera-08.png"
                alt="カメラ設置図"
                loading="lazy"
              />
            </picture>
          </div>
        </div>

        <div className="row">
          <div className="h-wrapper">
            <h3>NNカメラシステム駐車場</h3>
          </div>
        </div>

        <div className="row">
          <div className="container examples">
            <picture>
              <source
                srcSet="/assets/images/common/service-nncamera-09.webp"
                type="image/webp"
              />
              <img
                src="/assets/images/common/service-nncamera-09.png"
                alt="NNカメラシステム駐車場写真"
                loading="lazy"
              />
            </picture>
          </div>
        </div>

        <div className="row">
          <div className="h-wrapper" id="specs">
            <h2>4Kカメラと周辺機器の仕様</h2>
          </div>
        </div>

        <div className="row">
          <div className="h-wrapper">
            <h3>4Kカメラ</h3>
          </div>
        </div>

        <div className="row">
          <div className="container specs">
            <picture>
              <source
                srcSet="/assets/images/common/service-nncamera-10.webp"
                type="image/webp"
              />
              <img
                src="/assets/images/common/service-nncamera-10.png"
                alt="4Kカメラ写真"
                loading="lazy"
              />
            </picture>
          </div>
        </div>

        <div className="row">
          <div className="h-wrapper">
            <h3>4Kカメラ外観図</h3>
          </div>
        </div>

        <div className="row">
          <div className="container specs">
            <div className="spec-section">
              <picture>
                <source
                  srcSet="/assets/images/common/service-nncamera-11.webp"
                  type="image/webp"
                />
                <img
                  src="/assets/images/common/service-nncamera-11.png"
                  alt="4Kカメラ外観図"
                  loading="lazy"
                />
              </picture>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="h-wrapper">
            <h3>標準仕様</h3>
          </div>
        </div>

        <div className="row">
          <div className="container specs">
            <div className="spec-section">
              <div className="spec-group">
                <h6 className="spec-category">4Kカメラ</h6>
                <table className="spec-table">
                  <tbody>
                    <tr>
                      <th>イメージセンサー</th>
                      <td>1/2.8型 プログレッシブ CMOS</td>
                    </tr>
                    <tr>
                      <th rowSpan={3}>レンズ</th>
                      <td>電動ズーム：2.7～13.5mm F1.4</td>
                    </tr>
                    <tr>
                      <td>水平視野：107°～H33°</td>
                    </tr>
                    <tr>
                      <td>垂直視野：58°～18°</td>
                    </tr>
                    <tr>
                      <th rowSpan={2}>最低被写体照度</th>
                      <td>カラー：0.012Lux@F1.4</td>
                    </tr>
                    <tr>
                      <td>白黒：0Lux with IR on</td>
                    </tr>
                    <tr>
                      <th>WDR</th>
                      <td>120dB Super WDR</td>
                    </tr>
                    <tr>
                      <th>シャッタースピード</th>
                      <td>1/100000秒 ～ 1秒</td>
                    </tr>
                    <tr>
                      <th>赤外線距離</th>
                      <td>照射距離 50m</td>
                    </tr>
                    <tr>
                      <th>デイ／ナイトモード</th>
                      <td>デイ／ナイト／自動／カスタマイズ／スケジュール</td>
                    </tr>
                    <tr>
                      <th>最大解像度</th>
                      <td>3840×2160</td>
                    </tr>
                    <tr>
                      <th rowSpan={2}>動作環境</th>
                      <td>動作温度：-40℃～60℃</td>
                    </tr>
                    <tr>
                      <td>動作湿度：0～95％（非凝縮）</td>
                    </tr>
                    <tr>
                      <th>電源供給</th>
                      <td>PoE（802.3af）／DC 12V ±10％</td>
                    </tr>
                    <tr>
                      <th rowSpan={2}>消費電力</th>
                      <td>6W MAX</td>
                    </tr>
                    <tr>
                      <td>9W MAX（IRオン時）</td>
                    </tr>
                    <tr>
                      <th>保護等級</th>
                      <td>IP67 防水防塵性／IK10 耐衝撃性金属ハウジング</td>
                    </tr>
                    <tr>
                      <th rowSpan={2}>サイズ・重量</th>
                      <td>サイズ：Φ76mm × 239.5mm</td>
                    </tr>
                    <tr>
                      <td>重量：825g</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="spec-group">
                <h6 className="spec-category">Edgeデバイス</h6>
                <table className="spec-table">
                  <tbody>
                    <tr>
                      <th rowSpan={2}>ストレージ</th>
                      <td>RAM：LPDDR4x / LPDDR5、6GB</td>
                    </tr>
                    <tr>
                      <td>ROM：UFS128GB</td>
                    </tr>
                    <tr>
                      <th>電源供給</th>
                      <td>DC 12V</td>
                    </tr>
                    <tr>
                      <th>消費電力</th>
                      <td>53W MAX</td>
                    </tr>
                    <tr>
                      <th>サイズ</th>
                      <td>135mm × 115mm × 55mm</td>
                    </tr>
                    <tr>
                      <th>動作温度範囲</th>
                      <td>-20℃～＋55℃</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="spec-group">
                <h6 className="spec-category">PoEハブ</h6>
                <table className="spec-table">
                  <tbody>
                    <tr>
                      <th rowSpan={2}>ポート構成</th>
                      <td>8×10/100Mbps PoEポート（RJ45）</td>
                    </tr>
                    <tr>
                      <td>+2×100Mbpsアップリンクポート</td>
                    </tr>
                    <tr>
                      <th rowSpan={4}>対応規格</th>
                      <td>IEEE802.3 10Base-T</td>
                    </tr>
                    <tr>
                      <td>IEEE802.3i 10Base-T</td>
                    </tr>
                    <tr>
                      <td>IEEE802.3u 100Base-TX</td>
                    </tr>
                    <tr>
                      <td>IEEE802.3x</td>
                    </tr>
                    <tr>
                      <th rowSpan={2}>スイッチング性能</th>
                      <td>スイッチング容量：2Gbps（非閉塞）</td>
                    </tr>
                    <tr>
                      <td>パケット転送率：1.488Mpps（@64byte）</td>
                    </tr>
                    <tr>
                      <th rowSpan={2}>電源</th>
                      <td>電源内蔵</td>
                    </tr>
                    <tr>
                      <td>AC100～240V、50-60Hz、2.2A</td>
                    </tr>
                    <tr>
                      <th>PoE規格</th>
                      <td>IEEE802.3af/at</td>
                    </tr>
                    <tr>
                      <th rowSpan={2}>最大PoE給電</th>
                      <td>最大給電：120W</td>
                    </tr>
                    <tr>
                      <td>最大給電（1ポートあたり）：30W</td>
                    </tr>
                    <tr>
                      <th rowSpan={2}>動作環境</th>
                      <td>動作温度：-20℃～55℃</td>
                    </tr>
                    <tr>
                      <td>動作湿度：10%～90%（結露なし）</td>
                    </tr>
                    <tr>
                      <th rowSpan={2}>保護等級</th>
                      <td>ポート雷保護：4KV 8/20us</td>
                    </tr>
                    <tr>
                      <td>保護等級：IP30</td>
                    </tr>
                    <tr>
                      <th rowSpan={2}>サイズ・重量</th>
                      <td>サイズ：195mm(W) × 130mm(D) × 40mm(H)</td>
                    </tr>
                    <tr>
                      <td>重量：0.7kg</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="spec-group">
                <h6 className="spec-category">BOX</h6>
                <table className="spec-table">
                  <tbody>
                    <tr>
                      <th rowSpan={3}>サイズ・重量</th>
                      <td>サイズ：372mm(W) × 172mm(D) × 600mm(H)</td>
                    </tr>
                    <tr>
                      <td>重量：BOX本体11kg、ベース3kg</td>
                    </tr>
                    <tr>
                      <td>材質：鉄</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p className="spec-note">
                ※標準仕様は2025/4/1現在。製品の仕様は予告なく変更される場合があります。
              </p>
            </div>
          </div>
        </div>

        <ServiceFooter />
      </div>
    </Layout>
  )
}

export default NNCamera
